import { Injectable } from "@angular/core";
import { resource } from "@util/resource";
import { resolve } from "@util/resource/resolve";
import { AccountsService } from "@services/accounts.service";
import { Observable } from "rxjs";
import { first, map } from "rxjs/operators";
import { AiAgentChatLine } from "./ai-agent-chat.store";
import { AiAgentUiContext } from "./ai-agent-ui-context.service";
import {
  AiAgentProduct,
  normalizeAiAgentProduct,
} from "./ai-agent-product";

export interface AiAgentMessageDto {
  id?: string;
  role: string;
  content: string;
  createdAt?: string;
}

export interface AiAgentSession {
  sessionId: string;
  wineryId?: string;
  accountId?: string;
  product?: string;
  createdAt?: string;
  messages?: AiAgentMessageDto[];
}

export interface AiAgentSessionSummary {
  sessionId: string;
  createdAt?: string;
  updatedAt?: string;
  title?: string;
  messageCount?: number;
}

export type AiAgentSseHandler = (eventName: string, data: any) => void;

@Injectable({ providedIn: "root" })
export class AiAgentApiService {
  constructor(private accounts: AccountsService) {}

  createSession(product: AiAgentProduct = "waine"): Observable<AiAgentSession> {
    return resource("crm://ai-agent/sessions").post<AiAgentSession>({
      product: normalizeAiAgentProduct(product),
    });
  }

  listSessions(
    limit: number = 40,
    product: AiAgentProduct = "waine"
  ): Observable<AiAgentSessionSummary[]> {
    return resource("crm://ai-agent/sessions")
      .params({
        limit: String(limit),
        product: normalizeAiAgentProduct(product),
      })
      .get<AiAgentSessionSummary[]>();
  }

  getSession(
    sessionId: string,
    product: AiAgentProduct = "waine"
  ): Observable<AiAgentSession> {
    return resource("crm://ai-agent/sessions/id")
      .params({
        id: sessionId,
        product: normalizeAiAgentProduct(product),
      })
      .get<AiAgentSession>();
  }

  /** Map backend session messages to UI chat lines. */
  toChatLines(session: AiAgentSession | null | undefined): AiAgentChatLine[] {
    const rows = session?.messages;
    if (!Array.isArray(rows)) {
      return [];
    }
    return rows
      .filter((m) => m && (m.role === "user" || m.role === "assistant") && typeof m.content === "string")
      .map((m) => ({
        role: m.role as "user" | "assistant",
        text: m.content,
      }));
  }

  async streamMessage(
    sessionId: string,
    message: string,
    onEvent: AiAgentSseHandler,
    uiContext?: AiAgentUiContext | null,
    product: AiAgentProduct = "waine"
  ): Promise<void> {
    const url = resolve("crm://ai-agent/sessions/id/messages", { id: sessionId });
    const token = await this.accounts.authorization$
      .pipe(
        first(),
        map((auth) => (auth ? auth.token : null))
      )
      .toPromise();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    if (token) {
      headers["Authorization"] = token;
    }

    const body: {
      message: string;
      product: string;
      uiContext?: AiAgentUiContext;
    } = {
      message,
      product: normalizeAiAgentProduct(product),
    };
    if (uiContext) {
      body.uiContext = uiContext;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok || !response.body) {
      const text = await response.text();
      throw new Error(text || `AI agent stream failed (${response.status})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let eventName = "message";
    let dataLines: string[] = [];

    const flush = () => {
      if (!dataLines.length) {
        return;
      }
      const raw = dataLines.join("\n");
      dataLines = [];
      let data: any = raw;
      try {
        data = JSON.parse(raw);
      } catch {
        /* keep string */
      }
      onEvent(eventName, data);
      eventName = "message";
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n");
      buffer = parts.pop() || "";
      for (const line of parts) {
        if (line.startsWith("event:")) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trim());
        } else if (line === "") {
          flush();
        }
      }
    }
    flush();
  }
}
